/**
 * @author: chenlv
 * @date: 2025/01/22
 * @description: 维度筛选器组件
 */
import React, { useMemo } from 'react';
import classnames from 'classnames';
import { Select } from 'tea-component';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { isEmpty } from '@intelligence/v3/utils/common';
import { getDimensionOptionsBySource } from './utils';

import type { TDimension } from '../../types';

import './index.scss';

interface IDimensionSelectProps extends IBaseProps {
  /** 品类数据源 */
  genreSource: IGenreSource;
  /** 当前选中值 */
  value: TDimension;
  /** 变更回调 */
  onChange?: ((value: TDimension) => void) | null;
}

/**
 * DimensionSelect 维度筛选器组件
 * @description 根据品类数据源动态切换维度选项
 */
const DimensionSelect: React.FC<IDimensionSelectProps> = (props) => {
  const { style, className, genreSource, value, onChange } = props;

  const { formatMessage } = useIntlExtend();

  /**
   * 根据数据源获取维度选项（转换为 Select 组件需要的格式）
   */
  const dimensionOptions = useMemo(() => {
    const options = getDimensionOptionsBySource(genreSource);

    if (isEmpty(options)) {
      return [];
    }

    return options.map((option) => ({
      value: option?.value || '',
      text: formatMessage({ id: option?.i18nKey || '' }),
    }));
  }, [genreSource, formatMessage]);

  /**
   * 处理选项变更
   */
  const handleChange = (payload: string): void => {
    if (payload !== value) {
      onChange?.(payload as TDimension);
    }
  };

  /**
   * 渲染按钮内容（前缀文案 + 选中值）
   */
  const renderButton = (selectedOption: { value: string; text: string } | null): React.ReactNode => {
    const labelText = formatMessage({ id: 'dbi_casual_dimension_by_genre' });
    const contentText = selectedOption?.text || '';

    return (
      <div className="dbi-dimension-select__button">
        <span className="dbi-dimension-select__prefix">{labelText}</span>
        <span className="dbi-dimension-select__content">{contentText}</span>
      </div>
    );
  };

  return (
    <div style={style} className={classnames('dbi-dimension-select', className)}>
      <Select
        matchButtonWidth
        size="m"
        appearance="button"
        // @ts-ignore
        button={renderButton}
        options={dimensionOptions}
        value={value}
        onChange={handleChange}
      />
    </div>
  );
};

export default DimensionSelect;
